import clsx from 'clsx';
import React, { useCallback } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { useForm } from 'react-hook-form';
import { CreateStockItemFormData, CreateStockItemSchema } from '../schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { SAMPLE_CATEGORIES } from '../constants/sampleData';

interface CreateStockItemDialogProps {
    show: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const CreateStockItemDialog: React.FC<CreateStockItemDialogProps> = ({ show, onClose, onSuccess }) => {
    const { register, handleSubmit, formState: { errors }, reset } = useForm<CreateStockItemFormData>({
        resolver: zodResolver(CreateStockItemSchema),
        defaultValues: {
            quantity: 0,
            purchasingPrice: 0,
            sellingPrice: 0,
        }
    });

    const handleClose = () => {
        reset();
        onClose();
    }

    const onSubmit = useCallback((data: CreateStockItemFormData) => {
        toast.loading('Creating stock item...', { id: 'create-stock-item' });

        // TODO: Implement API call
        console.log('Creating stock item:', data);

        // Simulate API call
        setTimeout(() => {
            handleClose();
            toast.success('Stock item created successfully', { id: 'create-stock-item' });
            onSuccess?.();
        }, 1000);
    }, [onSuccess]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Create Stock Item</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" style={{ height: '100%' }}>
                    <div className="action-sheet-content flex-grow-1">
                        {/* Item Name */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Item Name: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.name && 'is-invalid')}
                                    id="name"
                                    placeholder="Item name"
                                    {...register('name')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.name && <div className="invalid-feedback">{errors.name.message}</div>}
                            </div>
                        </div>

                        {/* Category */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="category" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Category: </label>
                            <div className="col-sm-8">
                                <select
                                    className={clsx("form-control app-form-control rounded-1", errors?.category && 'is-invalid')}
                                    id="category"
                                    {...register('category')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                >
                                    <option value="">Select category</option>
                                    {SAMPLE_CATEGORIES.map((category) => (
                                        <option key={category.id} value={category.name}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                                {errors?.category && <div className="invalid-feedback">{errors.category.message}</div>}
                            </div>
                        </div>

                        {/* Quantity & Unit */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="quantity" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Quantity: </label>
                            <div className="col-sm-8 d-flex">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors?.quantity && 'is-invalid')}
                                    id="quantity"
                                    placeholder="0"
                                    {...register('quantity', { valueAsNumber: true })}
                                    style={{ fontSize: '14px', height: '35px', width: '80px' }}
                                />
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1 ms-2", errors?.unit && 'is-invalid')}
                                    id="unit"
                                    placeholder="Unit (e.g., pcs, kg)"
                                    {...register('unit')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                            </div>
                        </div>
                        {(errors?.quantity || errors?.unit) && (
                            <div className="row mb-3">
                                <div className="col-sm-4"></div>
                                <div className="col-sm-8">
                                    {errors?.quantity && <div className="invalid-feedback d-block">{errors.quantity.message}</div>}
                                    {errors?.unit && <div className="invalid-feedback d-block">{errors.unit.message}</div>}
                                </div>
                            </div>
                        )}

                        {/* Item Code */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="itemCode" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Item Code: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors?.itemCode && 'is-invalid')}
                                    id="itemCode"
                                    placeholder="Optional item code"
                                    {...register('itemCode')}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.itemCode && <div className="invalid-feedback">{errors.itemCode.message}</div>}
                            </div>
                        </div>

                        {/* Purchasing Price */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="purchasingPrice" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Purchase Price: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    step="0.01"
                                    className={clsx("form-control app-form-control rounded-1", errors?.purchasingPrice && 'is-invalid')}
                                    id="purchasingPrice"
                                    placeholder="0.00"
                                    {...register('purchasingPrice', { valueAsNumber: true })}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.purchasingPrice && <div className="invalid-feedback">{errors.purchasingPrice.message}</div>}
                            </div>
                        </div>

                        {/* Selling Price */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="sellingPrice" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Selling Price: </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    step="0.01"
                                    className={clsx("form-control app-form-control rounded-1", errors?.sellingPrice && 'is-invalid')}
                                    id="sellingPrice"
                                    placeholder="0.00"
                                    {...register('sellingPrice', { valueAsNumber: true })}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors?.sellingPrice && <div className="invalid-feedback">{errors.sellingPrice.message}</div>}
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="form-group mt-auto d-grid">
                        <button type="submit" className="btn btn-success btn-block">
                            Create Stock Item
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default CreateStockItemDialog